import React from 'react';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import { classes } from 'src/modules/shared/utils/styles.util';

/** The only format the system color picker accepts. */
const PICKER_COLOR_PATTERN = /^#[0-9a-f]{6}$/i;

/** Color shown in the swatch while the typed value is not a full HEX color. */
const EMPTY_SWATCH_COLOR = '#ffffff';

/** Props for a HEX color field with a system color picker. */
interface ColorInputProps {
  /** Name of the color, used in the accessible names of the picker and HEX field. */
  label: string;
  /** Current HEX value, possibly incomplete while the user types. */
  value: string;
  /** Called with the typed value, or the picked color in upper case. */
  onChange: (value: string) => void;
  /** Whether the field is read-only. */
  disabled?: boolean;
  /** Whether the current value is invalid; sets `aria-invalid`. */
  invalid?: boolean;
  /** ID of the hint or error that describes the field. */
  describedBy?: string;
  /** Extra classes for the outer box. */
  className?: string;
}

/**
 * Compact color field: a swatch that opens the system color picker and a HEX
 * text field. The caller validates the value and shows any hint.
 *
 * @param props - Label, value, state and change callback.
 * @returns The color field.
 */
export const ColorInput: React.FC<ColorInputProps> = ({
  label,
  value,
  onChange,
  disabled = false,
  invalid = false,
  describedBy,
  className,
}) => {
  const { t } = useI18n();
  const swatchColor = PICKER_COLOR_PATTERN.test(value)
    ? value
    : EMPTY_SWATCH_COLOR;

  return (
    <div
      className={classes(
        'flex h-10 w-fit items-center gap-2 rounded-lg border bg-white pl-1.5 pr-2.5',
        invalid ? 'border-danger-300' : 'border-neutral-300',
        disabled && 'opacity-60',
        className
      )}
    >
      <label
        className={classes(
          'relative h-7 w-7 shrink-0 overflow-hidden rounded-md border border-neutral-200',
          disabled ? 'cursor-not-allowed' : 'cursor-pointer'
        )}
      >
        <span className="sr-only">
          {t('shared.colorInput.picker', { label })}
        </span>
        <input
          type="color"
          value={swatchColor}
          disabled={disabled}
          onChange={(event) => onChange(event.target.value.toUpperCase())}
          className="absolute -inset-2 h-11 w-11 cursor-pointer border-0 bg-transparent p-0 disabled:cursor-not-allowed"
        />
      </label>
      <input
        aria-label={t('shared.colorInput.hex', { label })}
        value={value}
        disabled={disabled}
        spellCheck={false}
        autoComplete="off"
        maxLength={7}
        aria-invalid={invalid}
        aria-describedby={describedBy}
        onChange={(event) => onChange(event.target.value)}
        className="w-20 border-0 bg-transparent p-0 text-sm font-medium uppercase text-neutral-900 outline-none focus:ring-0 disabled:cursor-not-allowed"
      />
    </div>
  );
};
